import React, { createContext, useContext, useEffect, useState } from "react";
import { fetchAndCacheEntitlements } from "../../components/CacheDB/fetchAndCacheEntitlements";

interface Entitlement {
  privilege: string;
  po_path: string;
  permission_object_id?: number;
  permission_object_name?: string;
  po_type_code: string;
  asset_id?: number;
  asset_group_id?: number;
  asset_group_name?: string;
  user_group_name?: string;
}

interface EntitlementsContextType {
  entitlements: Entitlement[];
  loading: boolean;
  hasLoadedOnce: boolean;
  email?: string;
  refetchEntitlements: () => Promise<void>;
}

const EntitlementsContext = createContext<EntitlementsContextType | undefined>(
  undefined
);

export const EntitlementsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [entitlements, setEntitlements] = useState<Entitlement[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [email, setEmail] = useState<string | undefined>();

  const refetchEntitlements = async () => {
    setLoading(true);

    try {
      //Get email from localStorage (set by login)
      let userEmail = localStorage.getItem("userEmail");
      if (!userEmail) {
        console.warn("User email not found in localStorage.");
        setEntitlements([]);
        return;
      }

      userEmail = userEmail.trim();
      setEmail(userEmail);

      const objectId = localStorage.getItem("userObjectId");
      if (!objectId) {
        console.warn("User object ID not found in localStorage.");
        setEntitlements([]);
        return;
      }

      const sessionKey = `entitlements-${objectId}`;

      //Try sessionStorage first (fast load if same session).
      // Require a non-empty array: `Array.isArray([])` is true, so the old
      // guard accepted a cached empty result and returned early, which pinned
      // the whole session to "no entitlements" -- every nav tab in Header
      // disappears and no refetch is ever attempted.
      const cachedSession = sessionStorage.getItem(sessionKey);
      if (cachedSession) {
        try {
          const parsed = JSON.parse(cachedSession);
          if (Array.isArray(parsed?.data) && parsed.data.length > 0) {
            setEntitlements(parsed.data);
            setHasLoadedOnce(true);
            setLoading(false);
            return;
          }
          console.warn("Empty entitlements in sessionStorage, refetching...");
          sessionStorage.removeItem(sessionKey);
        } catch {
          console.warn("Invalid sessionStorage data, refetching...");
        }
      }

      //Fetch fresh data from API
      const response = await fetchAndCacheEntitlements();
      const apiData = response?.data?.data || response?.data;

      if (Array.isArray(apiData) && apiData.length > 0) {
        setEntitlements(apiData);
        sessionStorage.setItem(sessionKey, JSON.stringify({ data: apiData }));
      } else {
        console.warn("No entitlements found in API response.");
        setEntitlements([]);
      }
    } catch (err) {
      console.error("Failed to fetch entitlements:", err);
      setEntitlements([]);
    } finally {
      setHasLoadedOnce(true);
      setLoading(false);
    }
  };

  useEffect(() => {
    refetchEntitlements();
  }, []);

  return (
    <EntitlementsContext.Provider
      value={{ entitlements, loading, hasLoadedOnce, email, refetchEntitlements }}
    >
      {children}
    </EntitlementsContext.Provider>
  );
};

export const useEntitlements = () => {
  const context = useContext(EntitlementsContext);
  if (!context) {
    throw new Error(
      "useEntitlements must be used within an EntitlementsProvider"
    );
  }
  return context;
};
